import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { IPeerReviewFirstAssessment } from '@publishing-engine/domain/models';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import { generateValidationSchema } from 'src/utils/validation';
import {
  Accordion,
  Banner,
  Button,
  Modal,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { Recommendation } from './Recommendation';
import { ReviewPreviewModal } from './ReviewPreviewModal';
import { useFormDraft } from 'src/api/mutations';
import { isReviewRescinded } from 'src/utils/reviews';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { useSubmitFirstAssessmentReview } from '../../api/commands';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { useKeycloak } from '../AuthHOC';
import { ReviewerFieldGroup } from './FieldGroup';
import FileComponent from '../Assessment/FileComponent';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';
import ReviewerFooter from './ReviewerFooter';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchUserProfileData as fetchUserProfileDetails } from 'src/api/signUpMutation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faInfoCircle
} from '@fortawesome/pro-regular-svg-icons';
import ScandinavianKeywordProfileOverlay from '../UserRegistrationSteps/StepThree/ScandinavianKeywordProfileOverlay';

interface Props {
  submitTrigger?: boolean;
}

const ScandinavianKeywordsRequiredModal = ({
  isOpen,
  onClose,
  onUpdateProfile
}: {
  isOpen: boolean;
  onClose: () => void;
  onUpdateProfile: () => void;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} width="520px">
    <Flex direction="column" gap={3} padding={6}>
      <Flex alignItems="center" gap={2}>
        <Box color="actionSecondaryCautionContent">
          <FontAwesomeIcon icon={faCircleExclamation} />
        </Box>
        <Text textStyle="headingGroup">Please update your profile</Text>
      </Flex>
      <Text textStyle="textContext">
        To proceed with submitting your review for this journal, please ensure
        your profile includes the required Scandinavian keywords. Without these,
        the review cannot be submitted.
      </Text>
      <Text textStyle="textContext">
        Please update your profile. You will be redirected back to this page to
        resume your review once your profile is updated.
      </Text>
      <Flex justifyContent="flex-start">
        <Button variant="primary" onClick={onUpdateProfile}>
          Update Profile
        </Button>
      </Flex>
    </Flex>
  </Modal>
);

const requiresSpecialtyAndScandinavianKeyword = (
  requiredUserProfileData?: Array<{ fieldName: string; fieldType: string }>
) => {
  const requiredFields = new Set(
    requiredUserProfileData?.map(field => field.fieldName) ?? []
  );

  return (
    requiredFields.has('specialtyName') &&
    requiredFields.has('scandinavianKeyword')
  );
};

const ReviewSubmissionForm: React.FC<Props> = ({ submitTrigger }) => {
  const {
    review,
    submissionId,
    reviewerFormSchema,
    submissionContext,
    organizationData,
    workflowResponse,
    firstAssessmentFormDraft,
    invalidateReviewContextData,
    availableActions,
    organizationId
  } = useReviewData();
  const ctxId = review.submissionContextId ?? '';
  const toast = useToast();
  const { currentUser } = useKeycloak();
  const queryClient = useQueryClient();
  const profileDetailsQueryKey = ['review-submission-profile-details'];
  const [shouldFetchProfileDetails, setShouldFetchProfileDetails] =
    useState(false);
  const {
    data: profileDetailsData,
    refetch: refetchProfileDetails,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: profileDetailsQueryKey,
    queryFn: fetchUserProfileDetails,
    enabled: Boolean(currentUser?.id) && shouldFetchProfileDetails
  });
  const [showScandinavianKeywordsModal, setShowScandinavianKeywordsModal] =
    useState(false);
  const [
    hasShownScandinavianKeywordsModal,
    setHasShownScandinavianKeywordsModal
  ] = useState(false);
  const [showScandinavianProfileOverlay, setShowScandinavianProfileOverlay] =
    useState(false);

  const specialtyAndKeywords =
    profileDetailsData?.data?.researchFocusAndExpertise?.specialtyAndKeywords ??
    [];
  const hasMissingScandinavianKeyword = specialtyAndKeywords.some(item =>
    Boolean(
      (item?.specialtyName || item?.specialtyId) && !item?.scandinavianKeyword
    )
  );
  const shouldValidateScandinavianKeywords =
    requiresSpecialtyAndScandinavianKeyword(
      submissionContext?.config?.userProfileConfig?.requiredUserProfileData
    );
  const shouldRequireScandinavianKeywords =
    shouldValidateScandinavianKeywords && hasMissingScandinavianKeyword;
  const isProfileDetailsPending =
    !shouldFetchProfileDetails || isLoading || isFetching;

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }

    queryClient.removeQueries({
      queryKey: profileDetailsQueryKey,
      exact: true
    });
    setShouldFetchProfileDetails(true);
  }, [currentUser?.id, queryClient]);

  useEffect(() => {
    if (isProfileDetailsPending) {
      return;
    }

    if (
      shouldRequireScandinavianKeywords &&
      !hasShownScandinavianKeywordsModal
    ) {
      setShowScandinavianKeywordsModal(true);
      setHasShownScandinavianKeywordsModal(true);
      return;
    }

    if (!shouldRequireScandinavianKeywords) {
      setShowScandinavianKeywordsModal(false);
      setHasShownScandinavianKeywordsModal(false);
    }
  }, [
    hasShownScandinavianKeywordsModal,
    isProfileDetailsPending,
    shouldRequireScandinavianKeywords
  ]);

  const handleUpdateProfile = () => {
    setShowScandinavianKeywordsModal(false);
    setShowScandinavianProfileOverlay(true);
  };

  const submitReviewMutation = useSubmitFirstAssessmentReview({
    submissionId,
    reviewId: review.id,
    organizationId,
    submissionContextId: ctxId,
    articleWorkflowId: workflowResponse?.articleWorkflow?.id!,
    invalidateReviewContextData,
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
    email: currentUser.email
  });

  const submitReview = async (formValues: IPeerReviewFirstAssessment) => {
    const attachments = formValues.reviewerAttachments?.length
      ? formValues.reviewerAttachments?.map(attachment => {
          return {
            ...attachment,
            visibility: 'show'
          };
        })
      : undefined;
    formValues.reviewerAttachments = attachments;

    const EditorAttachments = formValues.reviewerAttachmentsEditor?.length
      ? formValues.reviewerAttachmentsEditor?.map(attachment => {
          return {
            ...attachment,
            visibility: 'show'
          };
        })
      : undefined;
    formValues.reviewerAttachmentsEditor = EditorAttachments;

    const review = await submitReviewMutation.mutateAsync(formValues);

    if (review.isSuccess) {
      toast({
        variant: 'success',
        title: 'Review Submitted',
        description: submissionContext?.config?.reviewerRating
          ? 'Your Review Score will be available upon manuscript decision.'
          : 'Review scuccessfully submitted',
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Review Submitted Failed',
        isClosable: true
      });
    }
  };

  const schemaId =
    workflowResponse.articleWorkflow.formSchemas.reviewerFirstAssessment;

  const { workflow, caseData } = review;
  const isRescinded = isReviewRescinded(workflow.currentState);
  const firstAssessmentRescission = caseData.firstAssessmentRescission;

  const [isPreviewModalEnabled, setPreviewModalEnabled] =
    useState<boolean>(false);
  const [formValues, setFormValues] = useState<IPeerReviewFirstAssessment>();
  const reviewFormFields = new ReviewFormFields(
    reviewerFormSchema.documentSchema
  );

  const formDraftMutation = useFormDraft();
  const configs = {
    Recommendation: reviewFormFields.getFieldConfig('Recommendation'),
    ReviewerAttachmentsEditor: reviewFormFields.getFieldConfig(
      'ReviewerAttachmentsEditor'
    ),
    // submission scores
    Originality: reviewFormFields.getFieldConfig('Originality'),
    Significance: reviewFormFields.getFieldConfig('Significance'),
    Rigour: reviewFormFields.getFieldConfig('Rigour'),
    Clarity: reviewFormFields.getFieldConfig('Clarity'),
    Impact: reviewFormFields.getFieldConfig('Impact'),

    FeedbackForAuthors: reviewFormFields.getFieldConfig('FeedbackForAuthors'),
    ReviewerAttachments: reviewFormFields.getFieldConfig('ReviewerAttachments'),

    MissingReference: reviewFormFields.getFieldConfig('MissingReference'),
    ConflictOfInterest: reviewFormFields.getFieldConfig('ConflictOfInterest'),
    CanArticleBeShortened: reviewFormFields.getFieldConfig(
      'CanArticleBeShortened'
    ),
    MeritFurtherCoverageUponPublication: reviewFormFields.getFieldConfig(
      'MeritFurtherCoverageUponPublication'
    ),
    AreaOfExpertise: reviewFormFields.getFieldConfig('AreaOfExpertise'),
    BetterSuitedForAnotherJournal: reviewFormFields.getFieldConfig(
      'BetterSuitedForAnotherJournal'
    ),
    FeedbackForEditors: reviewFormFields.getFieldConfig('FeedbackForEditors'),
    ArticleWellWritten: reviewFormFields.getFieldConfig('ArticleWellWritten'),
    WebOfScienceOptIn: reviewFormFields.getFieldConfig('WebOfScienceOptIn'),
    ReceiveFeedback: reviewFormFields.getFieldConfig('ReceiveFeedback'),
    ReviewConfirmation: reviewFormFields.getFieldConfig('ReviewConfirmation'),
    PeerReviewTransparency: reviewFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    ReviewerAnonymity: reviewFormFields.getFieldConfig('ReviewerAnonymity'),
    ConsentToReInvite: reviewFormFields.getFieldConfig('ConsentToReInvite'),
    ReviewersEthicalPolicy: reviewFormFields.getFieldConfig(
      'ReviewersEthicalPolicy'
    ),

    UsedAI: reviewFormFields.getFieldConfig('UsedAI')
  };

  const schema = generateValidationSchema(configs);

  const methods = useForm<IPeerReviewFirstAssessment>({
    defaultValues: firstAssessmentFormDraft
      ? firstAssessmentFormDraft
      : caseData.firstAssessment || {},
    resolver: ajvResolver(schema)
  });

  const { handleSubmit, getValues } = methods;
  const { reviewerAttachments, reviewerAttachmentsEditor } = getValues();

  const isReviewerAttachmentUploading = useCallback(() => {
    if (!configs.ReviewerAttachments || !reviewerAttachments) {
      return false;
    }

    return reviewerAttachments.some(attachment => attachment.isUploading);
  }, [reviewerAttachments, configs]);

  const isReviewerAttachmentsEditorUploading = useCallback(() => {
    if (!configs.ReviewerAttachmentsEditor || !reviewerAttachmentsEditor) {
      return false;
    }

    return reviewerAttachmentsEditor.some(attachment => attachment.isUploading);
  }, [reviewerAttachmentsEditor, configs]);

  const isReviewSubmissionAllowed =
    !isReviewerAttachmentUploading() &&
    availableActions.contains('SubmitFirstAssessment') &&
    !isReviewerAttachmentsEditorUploading() &&
    availableActions.contains('SubmitFirstAssessment');

  const onSubmit = () => {
    if (shouldRequireScandinavianKeywords) {
      closePreviewModal();
      setShowScandinavianKeywordsModal(true);
      return;
    }

    submitReview(getValues());
  };

  const submitFormDraft = async () => {
    const formData = getValues();

    const response = await formDraftMutation.mutateAsync({
      entityId: review.id,
      entityType: 'review',
      formType: 'reviewerFirstAssessment',
      formSchemaId: schemaId!,
      data: formData
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Draft Saved',
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Draft Save Unsuccessful',
        description:
          'We encountered an issue while saving your draft. Please try again.',
        isClosable: true
      });
    }
  };

  const openPreviewModal = () => {
    setFormValues(getValues());
    setPreviewModalEnabled(true);
  };

  const closePreviewModal = () => setPreviewModalEnabled(false);

  useEffect(() => {
    if (submitTrigger) {
      handleSubmit(openPreviewModal)();
    }
  }, [submitTrigger]);

  const footerRoot = document.getElementById('common-footer-root');

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(openPreviewModal, scrollToFirstError)}
          id="review-form"
        >
          <Flex flexDirection="column" gap={4} p={2}>
            <Flex flexDirection="column" gap={1}>
              <Banner
                content={
                  <Text textStyle={'textContext'}>
                    Thank you for agreeing to comment on this submission.
                  </Text>
                }
                heading="Your Review"
                customIcon={<FontAwesomeIcon icon={faInfoCircle} />}
                variant="neutral"
              />

              <Flex flexDirection={'column'} gap={3} mt={2}>
                {(submissionContext.config.reviewInformationLink ||
                  submissionContext.config.reviewGuidelineLink) && (
                  <>
                    <Accordion
                      chakraAccordionProps={{
                        style: {
                          display: 'flex',
                          flexDirection: 'column'
                        }
                      }}
                      items={[
                        {
                          title: 'Guidance For Reviewers',
                          content: (
                            <Flex direction="column" gap={2}>
                              <Text textStyle={'textContext'}>
                                {submissionContext.config.scopeLink && (
                                  <>
                                    {parseStringWithLinks(
                                      `Please read the journal scope and the requirements for specific article types before writing your review. You can find the scope, article types, and other information about ${submissionContext.data.name} [here](${submissionContext.config.scopeLink}). `
                                    )}
                                  </>
                                )}
                                {submissionContext.config
                                  .reviewGuidelineLink && (
                                  <>
                                    {parseStringWithLinks(
                                      `For instructions on how to write a review, please [click here](${submissionContext.config.reviewGuidelineLink}).`
                                    )}
                                  </>
                                )}
                              </Text>
                            </Flex>
                          )
                        }
                      ]}
                    />
                  </>
                )}
              </Flex>
            </Flex>

            {isRescinded && firstAssessmentRescission && (
              <Accordion
                data-testId="rescind-review"
                chakraAccordionProps={{
                  allowMultiple: true,
                  defaultIndex: [0, 1, 2],
                  style: {
                    display: 'flex',
                    flexDirection: 'column'
                  }
                }}
                items={[
                  {
                    title: 'Review Rescinded',
                    subtitle: `${firstAssessmentRescission.actionBy.firstName} ${firstAssessmentRescission.actionBy.lastName} has provided feedback to guide you through this decision.`,
                    content: (
                      <Flex direction="column" gap={3}>
                        {firstAssessmentRescission.feedback && (
                          <Text>{firstAssessmentRescission.feedback}</Text>
                        )}
                        {firstAssessmentRescission?.attachments &&
                          firstAssessmentRescission?.attachments?.length >
                            0 && (
                            <Flex flexDirection="column" gap={4}>
                              <Text textStyle={'headingItem'}>
                                Attached Files
                              </Text>
                              {firstAssessmentRescission.attachments.map(
                                file => (
                                  <FileComponent file={file} />
                                )
                              )}
                            </Flex>
                          )}
                      </Flex>
                    )
                  }
                ]}
              />
            )}
            <OutlineCard padding={4}>
              <Flex flexDirection="column" gap={2}>
                <ReviewerFieldGroup
                  fieldConfigs={[
                    configs.Originality,
                    configs.Clarity,
                    configs.Significance,
                    configs.Rigour,
                    configs.Impact
                  ]}
                  title="Evaluation Criteria"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </Flex>
            </OutlineCard>

            <OutlineCard padding={4}>
              <Flex flexDirection="column" gap={2}>
                <ReviewerFieldGroup
                  fieldConfigs={[
                    configs.FeedbackForAuthors,
                    configs.ReviewerAttachments
                  ]}
                  title="For Authors"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </Flex>
            </OutlineCard>

            <OutlineCard padding={4}>
              <Flex flexDirection="column" gap={2}>
                <ReviewerFieldGroup
                  fieldConfigs={[
                    configs.FeedbackForEditors,
                    configs.ReviewerAttachmentsEditor
                  ]}
                  title="For Editors"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </Flex>
            </OutlineCard>

            <OutlineCard padding={4}>
              <Flex flexDirection="column" gap={2}>
                <ReviewerFieldGroup
                  fieldConfigs={[
                    configs.MissingReference,
                    configs.ArticleWellWritten,
                    configs.AreaOfExpertise,
                    configs.BetterSuitedForAnotherJournal,
                    configs.ConflictOfInterest,
                    configs.CanArticleBeShortened,
                    configs.MeritFurtherCoverageUponPublication
                  ]}
                  title="Review Questionnaire"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </Flex>
            </OutlineCard>

            <OutlineCard padding={4}>
              <Flex flexDirection="column" gap={2}>
                <ReviewerFieldGroup
                  fieldConfigs={[
                    configs.WebOfScienceOptIn,
                    configs.PeerReviewTransparency,
                    configs.ReviewerAnonymity,
                    configs.ReceiveFeedback
                  ]}
                  title="Opt-Ins"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </Flex>
            </OutlineCard>

            <OutlineCard padding={4}>
              <Flex flexDirection="column" gap={2}>
                <ReviewerFieldGroup
                  fieldConfigs={[
                    configs.ReviewConfirmation,
                    configs.ReviewersEthicalPolicy,
                    configs.UsedAI
                  ]}
                  title="Compliance"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </Flex>
            </OutlineCard>

            {configs.Recommendation && (
              <OutlineCard padding={4}>
                <Flex flexDirection="column" gap={2}>
                  <Recommendation
                    config={configs.Recommendation}
                    textStyle="headingGroup"
                  />
                </Flex>
              </OutlineCard>
            )}

            {configs.ConsentToReInvite && (
              <OutlineCard padding={4}>
                <ReviewerFieldGroup
                  fieldConfigs={[configs.ConsentToReInvite]}
                  title="Consent"
                  organizationData={organizationData}
                  currentSubmissionContextId={submissionContext.id}
                  submissionId={submissionId}
                />
              </OutlineCard>
            )}
          </Flex>
          {footerRoot &&
            createPortal(
              <ReviewerFooter
                reviewId={review.id}
                onClickEvent={submitFormDraft}
                isDisabled={!isReviewSubmissionAllowed}
              />,
              footerRoot
            )}
        </form>
      </FormProvider>
      <ReviewPreviewModal
        isOpen={isPreviewModalEnabled}
        onClose={closePreviewModal}
        onSubmit={onSubmit}
        formValues={formValues!}
        formConfigs={configs}
        organizationData={organizationData}
        submissionContextId={ctxId}
      />
      <ScandinavianKeywordsRequiredModal
        isOpen={!isProfileDetailsPending && showScandinavianKeywordsModal}
        onClose={() => setShowScandinavianKeywordsModal(false)}
        onUpdateProfile={handleUpdateProfile}
      />
      <ScandinavianKeywordProfileOverlay
        isOpen={showScandinavianProfileOverlay}
        submissionContextId={ctxId}
        onClose={() => setShowScandinavianProfileOverlay(false)}
        onSaved={async () => {
          await refetchProfileDetails();
          setHasShownScandinavianKeywordsModal(false);
        }}
      />
    </>
  );
};

export default ReviewSubmissionForm;
